<script setup lang="ts">
import TrendDown from '@/assets/icon/down.svg'
import type { TraderDetailAssetStrengthTrend } from '~/api/trader-detail'

const props = defineProps<{
  tradingVolume?: TraderDetailAssetStrengthTrend | null
  to: string
}>()

const { t } = useAppI18n()

const EMPTY_TEXT = '--'

const normalizeText = (value: unknown) => (value === null || value === undefined ? '' : String(value).trim())

const title = computed(() => {
  const titleText = normalizeText(props.tradingVolume?.title) || t('82506') || '交易量'

  return /24h/i.test(titleText) ? titleText : `${titleText} (24h)`
})
const amountText = computed(() => normalizeText(props.tradingVolume?.amount))
const amountSymbol = computed(() => amountText.value.slice(0, 1))
const amountValue = computed(() => amountText.value.slice(1).trim())
const changePercent = computed(() => normalizeText(props.tradingVolume?.changePercent))
const changeDirection = computed(() => normalizeText(props.tradingVolume?.changeDirection))
const rankScore = computed(() => normalizeText(props.tradingVolume?.score) || EMPTY_TEXT)
const rankScoreMax = computed(() => normalizeText(props.tradingVolume?.scoreMax) || '100')
const hasTradingVolumeData = computed(() =>
  [amountText.value, changePercent.value, rankScore.value === EMPTY_TEXT ? '' : rankScore.value].some(Boolean),
)
</script>

<template>
  <NuxtLink
    :to="props.to"
    target="_blank"
    class="flex min-h-[212px] w-full cursor-pointer flex-col gap-[20px] rounded-[12px] border-[0.5px] border-[rgba(11,18,40,0.14)] bg-[#ffffff] p-[20px] text-[#292a2e] transition-colors hover:shadow-[0_12px_32px_rgba(8,19,62,0.08)]"
    aria-label="交易量"
  >
    <h3 class="text-[18px] leading-[26px] font-[600] whitespace-nowrap">
      {{ title }}
    </h3>

    <div class="flex min-h-[126px] flex-1 flex-col justify-between p-0">
      <div class="flex min-w-0 items-baseline gap-[4px]">
        <div
          class="inline-flex shrink-0 items-baseline justify-center gap-[2px] font-[WikiFX] break-words whitespace-nowrap text-[#292a2e]"
        >
          <template v-if="hasTradingVolumeData">
            <span v-if="amountText" class="text-[20px] leading-none font-[500]">{{ amountSymbol }}</span>
            <strong class="text-[36px] leading-none font-[700]">{{ amountValue || EMPTY_TEXT }}</strong>
          </template>
          <strong v-else class="text-[40px] leading-none font-[700]">{{ EMPTY_TEXT }}</strong>
        </div>
        <span
          v-if="hasTradingVolumeData && changePercent"
          class="inline-flex shrink-0 items-center text-[16px] leading-[24px] font-[500] whitespace-nowrap"
          :class="changeDirection === 'up' ? 'text-[#1f845a]' : 'text-[#e2483d]'"
        >
          {{ changePercent }}
          <TrendDown
            v-if="changeDirection === 'up' || changeDirection === 'down'"
            class="size-[12px] text-current [&_path]:fill-current"
            :class="{ 'rotate-180': changeDirection === 'up' }"
            aria-hidden="true"
          />
        </span>
      </div>

      <div
        class="flex flex-col gap-[8px] overflow-hidden rounded-[4px] border-[0.5px] border-[rgba(5,21,36,0.06)] bg-[#ffffff] px-[12px] py-[8px]"
      >
        <p class="text-[12px] leading-[18px] font-[400] text-[#505258]">{{ $t('84581') }}</p>
        <div class="inline-flex items-center gap-[2px] text-[13px] leading-[20px] text-[#7d818a]">
          <span class="inline-flex size-[16px] items-center justify-center text-[#292a2e]" aria-hidden="true">
            <svg class="h-[14px] w-[12px]" viewBox="0 0 12 13.851" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M0.666667 0H11.3333C11.5101 0 11.6797 0.0702379 11.8047 0.195262C11.9298 0.320286 12 0.489856 12 0.666667V13.5173C12.0001 13.573 11.9862 13.6277 11.9597 13.6766C11.9332 13.7255 11.8948 13.767 11.8482 13.7973C11.8015 13.8275 11.748 13.8457 11.6926 13.85C11.6371 13.8543 11.5814 13.8447 11.5307 13.822L6 11.3533L0.469333 13.8213C0.418602 13.844 0.363008 13.8536 0.307607 13.8493C0.252206 13.8451 0.198756 13.827 0.152118 13.7968C0.10548 13.7666 0.0671335 13.7252 0.0405664 13.6764C0.0139994 13.6276 5.45506e-05 13.5729 0 13.5173V0.666667C0 0.489856 0.0702379 0.320286 0.195262 0.195262C0.320286 0.0702379 0.489856 0 0.666667 0ZM6 7.66667L7.95933 8.69667L7.58533 6.51533L9.17 4.97L6.97933 4.65133L6 2.66667L5.02 4.65133L2.83 4.97L4.41467 6.51533L4.04133 8.69667L6 7.66667Z"
                fill="currentColor"
              />
            </svg>
          </span>
          <template v-if="hasTradingVolumeData">
            <strong class="text-[16px] leading-[24px] font-[600] text-[#292a2e]">{{ rankScore }}</strong>
            <span>/{{ rankScoreMax }}</span>
          </template>
          <span v-else class="text-[14px] leading-[22px] font-[400] text-[#292a2e]">{{ $t('84582') }}</span>
        </div>
      </div>
    </div>
  </NuxtLink>
</template>
